import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { scrollToTopIfSamePage } from '../utils/scrollToTop';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navbarRef = useRef(null);


  // Navigation data structure for better maintainability
  const navigationItems = {
    company: {
      label: 'Company',
      items: [
        { name: 'About Us', path: '/company', icon: 'bi-info-circle' },
        { name: 'Contact', path: '/contact', icon: 'bi-envelope' }
      ]
    },
    services: {
      label: 'Services',
      items: [
        { name: 'Generative AI', path: '/services/generative-ai', icon: 'bi-cpu' },
        { name: 'Machine Learning & Deep Learning', path: '/services/machine-learning', icon: 'bi-graph-up' },
        { name: 'Natural Language Processing (NLP)', path: '/services/nlp', icon: 'bi-chat-text' },
        { name: 'Chatbot Development', path: '/services/chatbot', icon: 'bi-robot' },
        { name: 'SaaS Product Development', path: '/services/saas', icon: 'bi-cloud' },
        { name: 'Predictive Analytics', path: '/services/predictive-analytics', icon: 'bi-graph-up-arrow' },
        { name: 'Custom Software & Web Development', path: '/services/custom-software', icon: 'bi-code-slash' },
        { name: 'UI/UX Design', path: '/services/ui-ux', icon: 'bi-palette' },
        { name: 'Staff Augmentation', path: '/services/staff-augmentation', icon: 'bi-people-fill' }
      ]
    },
    industries: {
      label: 'Industries',
      items: [
        { name: 'Healthcare', path: '/industries/healthcare', icon: 'bi-heart-pulse' },
        { name: 'Financial Services', path: '/industries/banking', icon: 'bi-bank' },
        { name: 'Retail & CPG', path: '/industries/retail', icon: 'bi-shop' },
        { name: 'Transport & Logistics', path: '/industries/transport', icon: 'bi-truck' },
        { name: 'Cybersecurity', path: '/industries/cybersecurity', icon: 'bi-shield-check' },
        { name: 'Education', path: '/industries/education', icon: 'bi-mortarboard' }
      ]
    },
    insights: {
      label: 'Insights',
      items: [
        { name: 'Case Studies', path: '/case-studies', icon: 'bi-folder' },
        { name: 'Testimonials', path: '/testimonials', icon: 'bi-chat-quote' }
      ]
    }
  };

  // Scroll handler with throttling for better performance
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Always show navbar background, but add shadow when scrolled
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle dropdown with keyboard support
  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  // Handle keyboard navigation
  const handleKeyDown = (event, dropdownName) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown(dropdownName);
    } else if (event.key === 'Escape') {
      setActiveDropdown(null);
    }
  };

  // Render dropdown menu items
  const renderDropdownItems = (items) => {
    return items.map((item, index) => (
      <li key={index}>
        <Link 
          className="dropdown-item-modern" 
          to={item.path}
          onClick={() => setActiveDropdown(null)}
        >
          <i className={`bi ${item.icon} me-2`}></i>
          <span>{item.name}</span>
          <div className="dropdown-item-glow"></div>
        </Link>
      </li>
    ));
  };

  return (
    <nav 
      ref={navbarRef}
      className={`navbar-modern ${isScrolled ? 'scrolled' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="navbar-container">
        {/* Brand/Logo */}
        <Link 
          className="navbar-brand-modern" 
          to="/" 
          aria-label="Vallen AI Home"
          onClick={() => scrollToTopIfSamePage(location.pathname, '/')}
        >
          <div className="brand-icon-modern">
            <img 
              src="/images/Vallen_AI_Logo.png"
              width="40"
              height="40"
              alt="Vallen AI Logo" 
              className="navbar-logo"
              loading="lazy"
              style={{
                height: '50px',
                width: 'auto',
                transition: 'all 0.3s ease'
              }}
            />
          </div>
          <span className="brand-text-full">
            <span style={{ color: '#0ea5e9', fontWeight: '600' }}>Vallen AI</span>
          </span>
        </Link>
        
        {/* Mobile Menu Toggle */}
        <button 
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-expanded={isMobileMenuOpen}
          aria-label="Toggle navigation menu"
          aria-controls="navbar-menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
        
        {/* Navigation Menu */}
        <div className={`navbar-menu ${isMobileMenuOpen ? 'show' : ''}`} id="navbar-menu">
          <ul className="navbar-nav-modern">
            {/* Services Dropdown */}
            <li className="nav-item-modern dropdown-modern">
              <button
                className={`nav-link-modern dropdown-toggle-modern ${activeDropdown === 'services' ? 'active' : ''}`}
                onClick={() => toggleDropdown('services')}
                onKeyDown={(e) => handleKeyDown(e, 'services')}
                aria-expanded={activeDropdown === 'services'}
                aria-haspopup="true"
              >
                <span>Services</span>
                <i className={`bi bi-chevron-down dropdown-arrow ${activeDropdown === 'services' ? 'rotate' : ''}`}></i>
              </button>
              <ul className={`dropdown-menu-modern ${activeDropdown === 'services' ? 'show' : ''}`}>
                {renderDropdownItems(navigationItems.services.items)}
              </ul>
            </li>

            {/* Industries Dropdown */}
            <li className="nav-item-modern dropdown-modern">
              <button
                className={`nav-link-modern dropdown-toggle-modern ${activeDropdown === 'industries' ? 'active' : ''}`}
                onClick={() => toggleDropdown('industries')}
                onKeyDown={(e) => handleKeyDown(e, 'industries')}
                aria-expanded={activeDropdown === 'industries'}
                aria-haspopup="true"
              >
                <span>Industries</span>
                <i className={`bi bi-chevron-down dropdown-arrow ${activeDropdown === 'industries' ? 'rotate' : ''}`}></i>
              </button>
              <ul className={`dropdown-menu-modern ${activeDropdown === 'industries' ? 'show' : ''}`}>
                {renderDropdownItems(navigationItems.industries.items)}
              </ul>
            </li>

            {/* Insights Dropdown */}
            <li className="nav-item-modern dropdown-modern">
              <button
                className={`nav-link-modern dropdown-toggle-modern ${activeDropdown === 'insights' ? 'active' : ''}`}
                onClick={() => toggleDropdown('insights')}
                onKeyDown={(e) => handleKeyDown(e, 'insights')}
                aria-expanded={activeDropdown === 'insights'}
                aria-haspopup="true"
              >
                <span>Insights</span>
                <i className={`bi bi-chevron-down dropdown-arrow ${activeDropdown === 'insights' ? 'rotate' : ''}`}></i>
              </button>
              <ul className={`dropdown-menu-modern ${activeDropdown === 'insights' ? 'show' : ''}`}>
                {renderDropdownItems(navigationItems.insights.items)}
              </ul>
            </li>

            {/* Company Dropdown */}
            <li className="nav-item-modern dropdown-modern">
              <button
                className={`nav-link-modern dropdown-toggle-modern ${activeDropdown === 'company' ? 'active' : ''}`}
                onClick={() => toggleDropdown('company')}
                onKeyDown={(e) => handleKeyDown(e, 'company')}
                aria-expanded={activeDropdown === 'company'}
                aria-haspopup="true"
              >
                <span>Company</span>
                <i className={`bi bi-chevron-down dropdown-arrow ${activeDropdown === 'company' ? 'rotate' : ''}`}></i>
              </button>
              <ul className={`dropdown-menu-modern ${activeDropdown === 'company' ? 'show' : ''}`}>
                {renderDropdownItems(navigationItems.company.items)}
              </ul>
            </li>


            {/* CTA Button */}
            <li className="nav-item-modern cta-item">
              <Link 
                className="btn-cta-modern" 
                to="/contact"
                onClick={() => scrollToTopIfSamePage(location.pathname, '/contact')}
              >
                <span>Let's Talk</span>
                <div className="btn-cta-glow"></div>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 